/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useEffect, useState } from "react";
import { fileUploadSources, optionsViewType } from "@Features/portal-settings";
import { formSectionType, getUrlFromField } from "./constants";
import { FormControl, FormHelperText } from "@mui/material";
import _ from "lodash";
import { getProjectTeamsUrl, locationUrl } from "@Services/dataManager";
import { QueryBuilder, executeQuery } from "@Services/queryBuilder";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { saveRemoteFiles } from "@Services/fileDownload";
import { TOAST_TYPE_WARNING } from "@Services/constants";
import { ValidationWrapperWeb } from "@Features/forms";
import AddURLModal from "./addURLModal";
import { FileManagerAsPopup } from "@Features/files";
import AddGeoLocation from "./addGeoLocationModal";
import useQuestionFields from "./useQuestionFields";
import useCameraCapture from "./useCameraCapture";
import CameraCaptureModals from "./cameraCaptureModals";
import { fieldRenderers, getFieldAns } from "./questionFieldsHelper";
import type {
  FieldAnswerViewProps,
  FieldGeoPoint,
  FieldModalStateArgs,
  FileUploadChangeArgs,
  OpenAddUserModalArgs,
  QuestionFieldRenderProps,
  RemoveAllUploadedFilesArgs,
  RemoveUploadedFileArgs,
  RemoveUserFromListArgs,
  SaveEditedImageArgs,
} from "./questionFieldsHelper";
import type { FieldValidatorHandle } from "../FormValidation/useValidationWrapper";
import type { FormFlowUser, QuestionField } from "@ServerTypes/forms/forms.schema";
import { toObjectIdString } from "@Helpers/schemas/common.schema";
import { RootState } from "@Reducers/index";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import Text from "@CubeComponents/html/Text/Text";
import { t } from "@i18n";
import type { TranslatedString } from "@i18n";
import { EmbeddedViewer } from "@Features/design-viewer";
import AddUser from "./addUserModal";

type SelectedFormFile = {
  fileVersion: string;
  fileId: string;
  resourceType: string;
  versionNumber: number;
  name: string;
};

/**
 * One entry of the File-Manager popup's selection. Distinct from `FieldFileValue` (a stored answer
 * entry): the manager identifies a file by `hierarchyId`, which is copied to `fileId` on the answer.
 */
type FileManagerSelection = {
  fileVersion?: string;
  hierarchyId?: string;
  resourceType?: string;
  versionNumber?: number;
  name?: string;
};

/** A project Team row as the look-up endpoint returns it. */
type TeamRow = { _id: string; name: string };

/** A project Location row as the look-up endpoint returns it. */
type LocationRow = { _id: string; name: string; parentId?: string | null };

/**
 * A question as this component accepts it.
 *
 * Deliberately NOT `QuestionField` / `RenderableQuestionField`. The component has three unrelated
 * callers: `virtualisedSections` passes a live instance question (the Zod `questionFieldSchema`
 * shape), portal-settings' `formPreview` passes its own `PreviewQuestionField` (every member
 * optional, because a dozen call sites feed it partially-built question objects), and the
 * file-upload grid passes a synthesised custom field. Only `QuestionField` requires `_id` and
 * `fieldType`, so the three are not mutually assignable; this names only the members every caller
 * supplies, and the two hand-off points that need the Zod shape assert into it.
 */
type QuestionFieldInput = {
  _id?: string;
  fieldType?: string;
  fieldLabel?: string;
  questionValue?: string;
  isMandatory?: boolean;
  placeholderText?: string;
  answer?: { value?: unknown };
  viewType?: string;
  // Written by the form-template builder onto user-type questions only; `addUserModal` reads it to
  // pick single- vs multi-select. Not modelled by `questionFieldSchema` (see `UsersFieldData`).
  selectionType?: "singleUser" | "multipleUser";
  // Overloaded across field types: the four user scopes on a users question, the five date modes on
  // a date-time one. Stays `string` for that reason.
  selectionScope?: string;
  lookUpAnsFieldType?: string;
  minFieldRange?: number | null;
  maxFieldRange?: number | null;
  uploadSource?: string;
  openIn?: string;
  allowManualSelection?: boolean;
  allowedFileTypes?: QuestionField["allowedFileTypes"];
  allowCameraCaptureOnly?: boolean;
};

/** The widest section shape all three callers satisfy. */
type SectionInput = {
  _id?: string;
  sectionCode?: string;
  tableDataSource?: unknown[];
  allowEditPreDefineRowInForm?: boolean;
};

export type FormQuestionFieldsProps = {
  eachField?: QuestionFieldInput;
  fieldIndex?: number;
  sectionIndex?: number;
  section?: SectionInput;
  source?: string;
  tableRowIndex?: number;
  tableRowId?: string | number | null;
  disabled?: boolean;
  showAnswersOnly?: boolean;
  formData?: { _id?: string; selectedForm?: unknown };
  userDetails?: { id?: string };
  extraParams?: Record<string, unknown>;
  templateSource?: string;
  spaceId?: string;
  width?: string;
  multiTextRows?: number;
  formValidator?: FieldValidatorHandle;
  isFileUploading?: Array<{ type?: string; uniqueId: string | number }>;
  modifySection?: Array<string | undefined>;

  handleInputChange?: (args: {
    answerValue?: unknown;
    fieldIndex?: number | null;
    sectionIndex?: number | null;
    eachField?: QuestionFieldInput;
    section?: SectionInput;
    tableRowIndex?: number;
    source?: string;
  }) => void;
  handleChangeAnswer?: (args: {
    source?: string;
    answerValue?: unknown;
    fieldIndex?: number | null;
    sectionIndex?: number | null;
    questionId?: string;
    tableRowIndex?: number;
  }) => void;
  handleCustomFieldData?:
    | ((args: {
        args?: Record<string, unknown>;
        eachField?: QuestionFieldInput;
        answerValue?: unknown;
        fieldIndex?: number | null;
        sectionIndex?: number | null;
        tableRowIndex?: number;
        source?: string;
      }) => void)
    | null;
  handleURLModalState?: (args: FieldModalStateArgs) => void;
  handleSaveUrlData?: (handleCustomFieldData: unknown, urlData: { URL?: string; displayText?: string }) => void;
  showUrlModal?: { state?: boolean; uniqueId?: string };
  showGeoLocationModal?: {
    state?: boolean;
    uniqueId?: string;
    eachField?: { allowManualSelection?: boolean };
  };
  setShowGeoLocationModal?: (args: { state: boolean }) => void;
  handleGeoLocationModalState?: (args: FieldModalStateArgs) => void;
  handleFileUploadChange?: (args: FileUploadChangeArgs) => void;
  handleRemoveUploadedFile?: (args: RemoveUploadedFileArgs) => void;
  handleRemoveAllUploadedFiles?: (args: RemoveAllUploadedFilesArgs) => void;
  removeUserFromList?: (args: RemoveUserFromListArgs) => void;
  saveEditedImage?: (args: SaveEditedImageArgs) => void;
  openAddUserModal?: (args: OpenAddUserModalArgs) => void;
  showAddUsersModal?: {
    status?: boolean;
    uniqueId?: string;
    source?: string;
    tableRowIndex?: number;
    fieldData?: { _id?: string; answer?: { value?: unknown } };
  };
  closeUsersModal?: () => void;
  handleAddUserClick?: (args: {
    handleCustomFieldData?: unknown;
    preSelectedUsers: unknown[];
    allSelectedUsers: unknown[];
  }) => void;
};

/**
 * What a widget hands back to `handleOnChange` / `handleOnBlur`. Every member is optional because
 * the shape is chosen by the widget, not by us: a DOM change event (`target`), a Dayjs instance
 * (`$d`), a tree-select selection (`itemData`), a File-Manager pick (`answer`), a geo point, or
 * `null` when a picker is cleared or a captured location removed.
 */
type FieldChangeArgs = {
  target?: { value?: unknown; checked?: boolean; name?: string };
  value?: unknown;
  answer?: SelectedFormFile[];
  itemData?: { id?: string; name?: string };
  $d?: unknown;
  current?: FieldGeoPoint | null;
  selected?: FieldGeoPoint | null;
  mapZoomLevel?: number;
} | null;

/** Outcome of translating a widget callback into a storable answer. */
type AnswerResult = { answerValue?: unknown; status: boolean; error?: string };

const FormQuestionFields = (props: FormQuestionFieldsProps) => {
  const {
    eachField,
    fieldIndex,
    sectionIndex,
    section = {},
    source = formSectionType.FORM,
    tableRowIndex = 0,
    tableRowId = null,
    disabled = false,
    handleCustomFieldData = null,
    showAnswersOnly = false,
    formData = {},
    userDetails = {},
    handleInputChange,
    handleChangeAnswer,
    handleURLModalState,
    handleSaveUrlData,
    showUrlModal = {},
    spaceId,
    showGeoLocationModal = {},
    setShowGeoLocationModal,
    extraParams = {},
    templateSource,
    showAddUsersModal = {},
    closeUsersModal,
    handleAddUserClick,
    openAddUserModal,
    handleRemoveAllUploadedFiles,
  } = props;
  const params = useParams() as { projectId: string };
  const dispatch = useDispatch();
  const lookUpQuery = new QueryBuilder().addParams("projectId", params.projectId);
  const userId = userDetails.id;
  const [teamsData, setTeamsData] = useState<Array<{ id: string; name: string }>>([]);
  const [locationData, setLocationData] = useState<Array<{ id: string; label: string; parentId?: string | null }>>([]);
  const [showFileManager, setShowFileManager] = useState(false);
  let formValidator: FieldValidatorHandle = props.formValidator || {};
  const tempFormValidator = useSelector((state: RootState) => state.formValidatorStore);
  if (!formValidator.validate) {
    formValidator = tempFormValidator;
  }
  // `eachField` has no default, so the destructure is on a possibly-absent object. The `!` is erased:
  // this component is only ever rendered for a question. `defaultValue` is spliced in as `undefined`
  // for the same reason it is in `RenderableQuestionField` — nothing writes it, so the effect that
  // reads it below never fires.
  const {
    _id,
    fieldType,
    lookUpAnsFieldType,
    minFieldRange,
    maxFieldRange,
    viewType = optionsViewType.LIST,
    selectionScope,
    defaultValue = null,
    uploadSource = fileUploadSources.LOCAL,
    openIn,
  } = eachField! as QuestionFieldInput & { defaultValue?: undefined };

  const {
    questionId,
    name,
    sectionId,
    currentAnswer,
    enabled,
    setOpenUrlPopUpState,
    // The `= {}` default is dead — the hook seeds this with `useState` and never returns `undefined`
    // — but it is emitted code, so it stays. The cast is erased.
    openUrlPopUpState = {} as { state: boolean; url: string; uniqueId?: string },
    formulaValue,
    concatenatedValue,
    // Bulk file actions: logic in the hook, UI here.
    handleDownloadAllFiles,
    isDownloadingAllFiles,
    handleDeleteAllFilesClick,
    isDeleteAllFilesModalOpen,
    closeDeleteAllFilesModal,
    confirmDeleteAllFiles,
    answerFiles,
  } = useQuestionFields({
    eachField: eachField as Parameters<typeof useQuestionFields>[0]["eachField"],
    tableRowId,
    extraParams,
    section: section as Parameters<typeof useQuestionFields>[0]["section"],
    tableRowIndex,
    source,
    formData: formData as Parameters<typeof useQuestionFields>[0]["formData"],
    userId,
    disabled,
    showAnswersOnly,
    templateSource,
    fieldIndex,
    sectionIndex,
    handleCustomFieldData,
    saveRemoteFiles,
    handleRemoveAllUploadedFiles,
  });

  // Capture flow for `image` fields: camera-or-browse chooser (camera only when the template says
  // so), live capture, review, and hand-off of the confirmed photo into the regular upload path
  // through a synthetic change event — `handleFileUploadChange` only reads `e.target.files[0]`.
  const cameraCapture = useCameraCapture({
    cameraOnly: !!eachField?.allowCameraCaptureOnly,
    allowedFileTypes: eachField?.allowedFileTypes,
    onBrowse: () => document.getElementById(`${name}`)?.click(),
    onImageReady: ({ blob, type, name: fileName }) => {
      // The photo goes out as-is — the upload path sends the File's bytes as a binary
      // multipart part, so no base64 conversion happens anywhere.
      const file = new File([blob], fileName, { type });
      props.handleFileUploadChange?.({
        e: { target: { files: [file], value: "" } } as unknown as FileUploadChangeArgs["e"],
        sectionId,
        eachField: eachField as FileUploadChangeArgs["eachField"],
        sectionIndex: sectionIndex!,
        fieldIndex: fieldIndex!,
        source,
        tableRowIndex,
        tableRowId,
        allowedFileTypes: eachField?.allowedFileTypes,
        formData: formData as FileUploadChangeArgs["formData"],
        handleCustomFieldData,
        extraParams,
      });
    },
  });

  const getTeamsData = async (): Promise<void> => {
    const res = await executeQuery<TeamRow>(getProjectTeamsUrl, lookUpQuery);
    const modifiedData = Array.isArray(res?.result)
      ? res.result.map((team: TeamRow) => ({
          id: team._id,
          name: team.name,
        }))
      : [];
    setTeamsData(modifiedData);
  };

  const getLocationData = async (): Promise<void> => {
    const res = await executeQuery<LocationRow>(locationUrl, lookUpQuery);
    const modifiedData = Array.isArray(res?.result)
      ? res.result.map((item: LocationRow) => ({
          ...item,
          id: item._id,
          label: item.name,
          parentId: item.parentId ?? null,
        }))
      : [];
    setLocationData(modifiedData);
  };

  useEffect(() => {
    if (fieldType !== "look-up") return;
    if (lookUpAnsFieldType === "Teams" && params.projectId) {
      getTeamsData();
    }
    if (lookUpAnsFieldType === "Location" && params.projectId) {
      getLocationData();
    }
  }, [fieldType, lookUpAnsFieldType, params.projectId]);

  useEffect(() => {
    if (defaultValue) {
      formValidator.validate!({ name, value: defaultValue });
    }
  }, [defaultValue]);

  const getMultiSelectAnswer = (args: FieldChangeArgs) => {
    if (viewType === optionsViewType.LIST) {
      const prevAns = (currentAnswer?.value as string[] | undefined) || [];
      return args!.target!.checked
        ? [...prevAns, args!.target!.name]
        : prevAns.filter((ans: string) => ans !== args!.target!.name);
    }
    return args!.target!.value;
  };

  const getDateTimeAnswer = (args: FieldChangeArgs): AnswerResult => {
    if (selectionScope === "currentDateTime") {
      return { answerValue: Date.now(), status: true };
    }
    let dateVal: number | null = null;
    if (_.get(args, "$d")) {
      dateVal = new Date(args!.$d as string | number | Date).getTime();
    } else if (args?.value instanceof Date) {
      dateVal = args.value.getTime();
    }
    if (
      (minFieldRange && dateVal && dateVal < minFieldRange) ||
      (maxFieldRange && dateVal && dateVal > maxFieldRange)
    ) {
      return { answerValue: null, status: false, error: "Invalid date cannot be saved" };
    }
    if (Number.isNaN(dateVal)) {
      return { status: false };
    }
    return { answerValue: dateVal, status: true };
  };
  const getLookUpAnswer = (args: FieldChangeArgs) => {
    if (lookUpAnsFieldType === "Teams") return args;

    if (lookUpAnsFieldType === "Location") {
      const prevAns = (currentAnswer?.value as Array<{ id?: string; name?: string }> | undefined) || [];
      return [...prevAns, { id: args!.itemData!.id, name: args!.itemData!.name }];
    }

    return currentAnswer?.value || [];
  };

  const getFileUploadAnswer = (args: FieldChangeArgs, currentAnswer?: { value?: unknown }) =>
    Array.isArray(currentAnswer?.value) ? [...currentAnswer.value, ...args!.answer!] : [...args!.answer!];

  const getAnswer = (args: FieldChangeArgs): AnswerResult => {
    let output: AnswerResult = { answerValue: "", status: true };

    // Every `args!.target!` below is an erased assertion. `args` is `null` only on the geoLocation
    // and date-time clear paths, and both land in their own branches — which is why the original
    // code could read `.target.value` unguarded here.
    if (["single-line", "multi-line", "single-select"].includes(fieldType as string)) {
      output.answerValue = args!.target!.value;
    } else if (fieldType == "multi-select") {
      output.answerValue = getMultiSelectAnswer(args);
    } else if (fieldType == "number") {
      const rawValue = (args!.target!.value as string).replace(/,/g, "");
      output.answerValue = rawValue === "" ? null : Number(rawValue);
    } else if (fieldType == "date-time") {
      const result = getDateTimeAnswer(args);
      if (!result.status) {
        return result;
      }
      output.answerValue = result.answerValue;
    } else if (fieldType == "look-up") {
      output.answerValue = getLookUpAnswer(args);
    } else if (fieldType == "fileUpload" && uploadSource === fileUploadSources.FILE_MANAGER) {
      output.answerValue = getFileUploadAnswer(args);
    } else if (fieldType == "geoLocation") {
      output.answerValue = args;
    }
    return output;
  };

  const handleOnBlur = (args: FieldChangeArgs) => {
    const res = getAnswer(args);
    if (res.status) {
      if (formData._id) {
        handleInputChange!({
          answerValue: res.answerValue,
          fieldIndex,
          sectionIndex,
          eachField,
          section,
          tableRowIndex,
          source,
        });
      }
    } else {
      dispatch(
        showToastNotification({
          // KNOWN BUG, preserved verbatim: this message is a bare English literal, not a `t(...)`
          // key, so it is never translated. The cast is erased; translating it would add a locale
          // key, which is a behaviour change and out of scope for a typing pass.
          content: res.error as TranslatedString,
          type: TOAST_TYPE_WARNING,
        }),
      );
    }
  };

  const handleOnChange = (args: FieldChangeArgs) => {
    const res = getAnswer(args);

    if (formValidator?.validate) {
      formValidator.validate({ name, value: res.answerValue });
    }
    if (res.status) {
      if (handleChangeAnswer) {
        handleChangeAnswer({
          source,
          answerValue: res.answerValue,
          fieldIndex,
          sectionIndex,
          questionId,
          tableRowIndex,
        });
      }
      if (handleCustomFieldData) {
        handleCustomFieldData({
          args: extraParams,
          eachField,
          answerValue: res.answerValue,
          fieldIndex,
          sectionIndex,
          tableRowIndex,
          source,
        });
      }
    }
  };

  const handleFileUploadClick = () => {
    if (uploadSource == fileUploadSources.LOCAL) {
      document.getElementById(`${name}`)?.click();
    } else {
      setShowFileManager(true);
    }
  };

  const handleUrlFieldClick = () => {
    const url = getUrlFromField(_.get(currentAnswer, "value.URL", ""));
    if (openIn == "popUp") {
      setOpenUrlPopUpState({ state: true, url, uniqueId: name });
    } else if (openIn == "sameTab") {
      window.location.href = url;
    } else {
      window.open(url);
    }
  };

  const handleRemoveLocation = () => {
    handleOnChange(null);
    handleOnBlur(null);
  };

  const getFieldAnsComponent = () => {
    const renderer = fieldRenderers[fieldType as string];
    // The cast is erased. `fieldRenderers` is keyed by `fieldType` and each entry declares the
    // concrete answer shape that fieldType stores, so no single props type the table exposes can be
    // both callable and assignable from all fifteen renderers; `never` is the value type that makes
    // the table assignment work, and the call site asserts into it. See `fieldRenderers`.
    const fieldComponent = renderer ? (
      renderer({
        ...props,
        projectId: params.projectId,
        formValidator,
        questionId,
        name,
        sectionId,
        currentAnswer,
        enabled,
        formulaValue,
        concatenatedValue,
        handleOnChange,
        handleOnBlur,
        handleRemoveLocation,
        lookUpQuery,
        teamsData,
        locationData,
        handleFileUploadClick,
        handleUrlFieldClick,
        openAddUserModal,
        handleDownloadAllFiles,
        isDownloadingAllFiles,
        handleDeleteAllFilesClick,
        openImageUploadOptions: cameraCapture.openImageCapture,
        formData,
        source,
      } as QuestionFieldRenderProps<never>)
    ) : (
      <></>
    );

    const showCustomValidation = ["look-up", "users", "fileUpload", "image", "url", "geoLocation"].includes(
      fieldType as string,
    );

    return showCustomValidation ? (
      <ValidationWrapperWeb value={currentAnswer.value} name={name} validator={formValidator}>
        {fieldComponent}
      </ValidationWrapperWeb>
    ) : (
      <FormControl fullWidth error={!!_.get(formValidator, `error.${name}`)}>
        {fieldComponent}
        {!!_.get(formValidator, `error.${name}`) && (
          <FormHelperText sx={{ marginInlineStart: 0 }}>{_.get(formValidator, `error.${name}`)}</FormHelperText>
        )}
      </FormControl>
    );
  };

  const handleFileSelectedCancelClick = () => {
    setShowFileManager(false);
  };

  const handleFileSelectedButtonClick = (args: Record<string, FileManagerSelection>) => {
    // Each `!` is erased. These five members are optional on a File-Manager entry in general, but a
    // file the picker returns as selected always carries them — which is what the original
    // unannotated reads assumed.
    const selectedFiles: SelectedFormFile[] = Object.values(args).map((file: FileManagerSelection) => ({
      fileVersion: file.fileVersion!,
      fileId: file.hierarchyId!,
      resourceType: file.resourceType!,
      versionNumber: file.versionNumber!,
      name: file.name!,
    }));
    const answer = [...((currentAnswer?.value as SelectedFormFile[]) || []), ...selectedFiles];
    handleOnChange({ answer });
    handleOnBlur({ answer });
    handleFileSelectedCancelClick();
  };

  const handleSaveLocation = ({
    current,
    selected,
    mapZoomLevel,
  }: {
    current: FieldGeoPoint | null;
    selected: FieldGeoPoint | null;
    mapZoomLevel: number;
  }) => {
    if (showGeoLocationModal?.eachField?.allowManualSelection) {
      handleOnChange({ current, selected, mapZoomLevel });
      handleOnBlur({ current, selected, mapZoomLevel });
    } else {
      handleOnChange({ current, selected: current, mapZoomLevel });
      handleOnBlur({ current, selected: current, mapZoomLevel });
    }
    setShowGeoLocationModal!({ state: false });
  };
  const deleteAllFilesPopup = () => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={
        <div className="flex gap-4">
          <ButtonComponent onClick={confirmDeleteAllFiles} buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
            <Text>{t("Confirm")}</Text>
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeDeleteAllFilesModal}>
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </div>
      }
      headerTitle={
        <div className="w-full flex justify-between rounded-md">
          <Text>{t("Delete all files")}</Text>
        </div>
      }
      isVisible
      onCancel={closeDeleteAllFilesModal}
      modelContent={
        <Text className="whitespace-normal">
          {t("This will permanently remove all {{count}} file(s) from this field. Are you sure ?", {
            count: answerFiles.length,
          })}
        </Text>
      }
      width="30%"
    />
  );

  return (
    <>
      {showAnswersOnly
        ? getFieldAns(
            props as FieldAnswerViewProps,
            currentAnswer,
            handleUrlFieldClick,
            params.projectId,
            handleDownloadAllFiles,
          )
        : getFieldAnsComponent()}
      {isDeleteAllFilesModalOpen && deleteAllFilesPopup()}
      {showFileManager && (
        <FileManagerAsPopup
          projectId={params.projectId}
          handleFileSelectedButtonClick={handleFileSelectedButtonClick}
          handleFileSelectedCancelClick={handleFileSelectedCancelClick}
          workspaceId={spaceId}
          selectedFilesData={currentAnswer.value}
          hideIssues
          hideTasks
          hideEstimates
          hideSchedules
          hideContracts
          hideExternalSpace
          enableFilesTypes={["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_FOLDER", "RESOURCE_TYPE_MERGED_FILE"]}
        />
      )}
      {cameraCapture.captureState !== "closed" && <CameraCaptureModals capture={cameraCapture} />}
      {showUrlModal?.state && name === showUrlModal.uniqueId && (
        <AddURLModal
          showUrlModal={showUrlModal}
          handleCustomFieldData={handleCustomFieldData}
          handleURLModalState={handleURLModalState as (params: { state: boolean }) => void}
          handleSaveUrlData={
            handleSaveUrlData as (
              handleCustomFieldData: unknown,
              urlData: { URL?: string; displayText?: string },
            ) => void
          }
          name={name}
        />
      )}
      {showGeoLocationModal?.state && name == showGeoLocationModal.uniqueId && (
        <AddGeoLocation
          setShowGeoLocationModal={setShowGeoLocationModal!}
          showGeoLocationModal={
            showGeoLocationModal as {
              state: boolean;
              uniqueId?: string;
              eachField: Pick<QuestionField, "allowManualSelection" | "captureLiveLocation">;
            }
          }
          handleSaveLocation={handleSaveLocation}
          name={name}
        />
      )}
      {openUrlPopUpState.state && openUrlPopUpState.uniqueId === name && (
        <ModalStateDialogComponent
          headerTitle={<div className="flex justify-between w-full">{t("View Url")}</div>}
          modelContent={<EmbeddedViewer id={_id} embedUrl={openUrlPopUpState.url} />}
          width="95%"
          height="95%"
          onCancel={() => setOpenUrlPopUpState({ state: false, url: "" })}
          isVisible={openUrlPopUpState.state}
        />
      )}
      {showAddUsersModal.status && name === showAddUsersModal.uniqueId && (
        <AddUser
          currentUsers={
            showAddUsersModal.source == formSectionType.TABLE_SECTION
              ? (((
                  section.tableDataSource![showAddUsersModal.tableRowIndex!] as
                    | Record<string, { value?: unknown } | undefined>
                    | undefined
                )?.[showAddUsersModal.fieldData!._id!]?.value ?? []) as FormFlowUser[])
              : ((showAddUsersModal.fieldData?.answer?.value ?? []) as FormFlowUser[])
          }
          closeUsersModal={closeUsersModal!}
          handleAddUserClick={handleAddUserClick!}
          fieldData={eachField}
          section={section}
          // The three ids below came out of Mongo — the route param, the loaded instance, and the
          // space the form lives in — so they are re-tagged rather than re-validated.
          // `toObjectIdString` is the sanctioned way to do that (it maps `undefined` through), and
          // is what keeps a bare `as ObjectIdString` out of this file.
          formId={toObjectIdString(formData._id)}
          spaceId={toObjectIdString(spaceId)}
          projectId={toObjectIdString(params.projectId)}
          handleCustomFieldData={handleCustomFieldData}
        />
      )}
    </>
  );
};
export default FormQuestionFields;
